import { Fragment, FunctionComponent } from 'react';
import {
    Card,
    CardContent,
    Divider,
    Link,
    Stack,
    Typography,
} from '@mui/material';
import { CardListRow } from '@/components/Card/CardListRow';
import { formatStarjackDate, formatStarjackDateWithTime } from '@/utils/time';
import { get } from 'lodash';
import { Order } from '@/models/order';
import { isOfTypeMoney } from '@/models/money';
import { downloadContentGeneric, getMoneyAsString } from '@/utils';
import {
    getInvoice,
    usePostReOrderKeycardsInternalMutation,
} from '@/api/v2/order';
import { useNotification } from '@starjack/features/notifications';
import { Customer } from '@/models/customer';
import { LoadingButton } from '@mui/lab';
import { KeycardStatus } from '@/models/keycard';

const orderKeyLabel: Record<string, string> = {
    id: 'Bestell-ID',
    displayId: 'Bestell-Anzeige-ID',
    creationDate: 'Erstellt am',
    paymentDate: 'Bezahlt am',
    status: 'Status',
    payAmount: 'Bezahlt',
    free: 'Frei',
    ['address.firstname']: 'Vorname',
    ['address.lastname']: 'Nachname',
    ['address.company']: 'Firma',
    ['address.street']: 'Straße',
    ['address.zip']: 'PLZ',
    ['address.city']: 'Ort',
    ['address.country']: 'Land',
    ['tickets[0].identificationType']: 'Produktionsart',
};

const orderKeySort: (keyof typeof orderKeyLabel)[] = [
    'id',
    'displayId',
    'creationDate',
    'paymentDate',
    'status',
    'payAmount',
    'free',
    'address.firstname',
    'address.lastname',
    'address.company',
    'address.street',
    'address.zip',
    'address.city',
    'address.country',
    'tickets[0].identificationType',
];

type CustomerOrderCardProps = {
    order: Order;
    customer: Customer;
};

export const CustomerOrderCard: FunctionComponent<CustomerOrderCardProps> = ({
    order,
}) => {
    const { addNotification } = useNotification();
    const [postReOrder, { isLoading }] =
        usePostReOrderKeycardsInternalMutation();
    const onDownload = async () => {
        const [downloadData, errorCode] = await getInvoice(order.id);

        if (errorCode === null) {
            downloadContentGeneric(
                `Rechnung von ${formatStarjackDate(order.creationDate)}.pdf`,
                downloadData
            );
        } else {
            if (errorCode === 404) {
                addNotification({
                    type: 'error',
                    message: 'Rechnung nicht gefunden',
                });
            } else if (errorCode >= 500) {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Herunterladen der Rechnung. Bitte später noch einmal versuchen',
                });
            }
        }
    };

    const onReOrder = () => {
        postReOrder({ orderId: order.id })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Keycards wurden neu bestellt',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim erneuten Bestellen. Bitte später noch einmal versuchen',
                });
            });
    };

    return (
        <Stack
            sx={{
                width: '100%',
            }}
            direction='row'
            spacing={2}
            alignItems='flex-start'
        >
            <Card
                sx={{
                    width: '75%',
                    maxWidth: 640,
                }}
            >
                <CardContent
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        bgcolor: 'divider',
                    }}
                >
                    <Typography variant='h6'>Bestellung</Typography>

                    <LoadingButton
                        onClick={onReOrder}
                        sx={{
                            ml: 'auto',
                        }}
                        loading={isLoading}
                        disabled={
                            !order.keycards.some(
                                (k) => k.status === KeycardStatus.PRODUCED
                            )
                        }
                        variant='contained'
                    >
                        Interne Keycards neu Bestellen
                    </LoadingButton>
                </CardContent>
                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                <Divider
                    sx={{
                        width: '100%',
                    }}
                />

                {orderKeySort.map((key) => {
                    const label = orderKeyLabel[key];
                    const value = get(order, key, '-');

                    let valueString;

                    if (value instanceof Date) {
                        valueString = formatStarjackDateWithTime(value);
                    } else if (isOfTypeMoney(value)) {
                        valueString = getMoneyAsString(value);
                    } else if (typeof value === 'boolean') {
                        valueString = value ? 'Ja' : 'Nein';
                    } else {
                        valueString = value?.toString();
                    }

                    if (!label) return null;

                    return (
                        <Fragment key={key}>
                            <CardListRow>
                                <Typography variant='subtitle2'>
                                    {orderKeyLabel[key] || key}:
                                </Typography>

                                <Typography
                                    sx={{
                                        mt: 2,
                                        ml: 2,
                                    }}
                                >
                                    {valueString}
                                </Typography>
                            </CardListRow>
                            <Divider
                                sx={{
                                    width: '100%',
                                }}
                            />
                        </Fragment>
                    );
                })}

                <CardListRow>
                    <Typography variant='subtitle2'>Rechnung:</Typography>
                    <Link
                        onClick={onDownload}
                        sx={{
                            cursor: 'pointer',
                            color: 'info.dark',
                            mt: 2,
                            ml: 2,
                        }}
                    >
                        Rechnung herunterladen
                    </Link>
                </CardListRow>
            </Card>
        </Stack>
    );
};
